import type { GuardStatus } from "@protein-fiber-guard/contracts";

export const SHARE_CARD_URL = "https://proteinfiberguard.com";

export type ShareCardInput = {
  mealType: string;
  proteinStatus: GuardStatus;
  fiberStatus: GuardStatus;
  oneFoodFix: { food: string; portion: string } | null;
};

const escapeXml = (value: string) => value.replace(/[&<>"']/g, (character) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;"
})[character]!);

const statusLabel = (status: GuardStatus) => status === "on_track" ? "ON TRACK" : status.toUpperCase();
const mealLabel = (mealType: string) => mealType.trim().replace(/[_-]+/g, " ").toUpperCase().slice(0, 24) || "MEAL";
const wrapFix = (value: string): [string, string] => {
  const words = value.trim().split(/\s+/); const lines: [string, string] = ["", ""]; let lineIndex: 0 | 1 = 0;
  for (const word of words) {
    const candidate = `${lines[lineIndex]} ${word}`.trim();
    if (lineIndex === 0 && candidate.length > 34) { lineIndex = 1; lines[1] = word; }
    else if (lineIndex === 1 && candidate.length > 38) { lines[1] = `${lines[1].slice(0, 34).trimEnd()}…`; break; }
    else lines[lineIndex] = candidate;
  }
  return [lines[0], lines[1]];
};

export const createShareCardSvg = (input: ShareCardInput): string => {
  const meal = escapeXml(mealLabel(input.mealType));
  const protein = escapeXml(statusLabel(input.proteinStatus));
  const fiber = escapeXml(statusLabel(input.fiberStatus));
  const fix = input.oneFoodFix
    ? `Add ${input.oneFoodFix.portion.trim()} ${input.oneFoodFix.food.trim().toLowerCase()}.`
    : "No extra fix needed for this check.";
  const [fixLineOne, fixLineTwo] = wrapFix(fix.slice(0, 120)).map(escapeXml) as [string, string];

  return `<svg xmlns="http://www.w3.org/2000/svg" width="1080" height="1350" viewBox="0 0 1080 1350" role="img" aria-labelledby="title description">
  <title id="title">Anonymous Protein and Fiber Guard result</title>
  <desc id="description">${meal} meal with protein ${protein}, fiber ${fiber}, and one food fix.</desc>
  <rect width="1080" height="1350" fill="#f4f0e7"/>
  <circle cx="900" cy="190" r="250" fill="#e5aa2c" opacity="0.13"/>
  <circle cx="160" cy="1160" r="300" fill="#ef5d39" opacity="0.10"/>
  <g transform="translate(76 72)">
    <circle cx="44" cy="44" r="44" fill="#12362f"/>
    <text x="44" y="54" text-anchor="middle" font-family="Arial, sans-serif" font-size="24" font-weight="700" fill="#ffffff">P•F</text>
    <text x="108" y="55" font-family="Georgia, serif" font-size="42" font-weight="700" fill="#12362f">Guard</text>
  </g>
  <text x="76" y="238" font-family="Arial, sans-serif" font-size="24" font-weight="700" letter-spacing="5" fill="#6a7b75">SHARE MY GUARD</text>
  <text x="76" y="335" font-family="Georgia, serif" font-size="76" font-weight="700" fill="#12362f">A ${meal.toLowerCase()} worth</text>
  <text x="76" y="425" font-family="Georgia, serif" font-size="76" font-weight="700" fill="#12362f">guarding.</text>
  <g transform="translate(76 500)">
    <rect width="448" height="250" rx="32" fill="#fffdf7" stroke="#12362f" stroke-opacity="0.12" stroke-width="2"/>
    <text x="34" y="58" font-family="Arial, sans-serif" font-size="22" font-weight="700" letter-spacing="4" fill="#6a7b75">PROTEIN</text>
    <text x="34" y="145" font-family="Arial, sans-serif" font-size="48" font-weight="700" fill="#ef5d39">${protein}</text>
    <text x="34" y="202" font-family="Arial, sans-serif" font-size="24" fill="#6a7b75">Daily guardrail check</text>
  </g>
  <g transform="translate(556 500)">
    <rect width="448" height="250" rx="32" fill="#fffdf7" stroke="#12362f" stroke-opacity="0.12" stroke-width="2"/>
    <text x="34" y="58" font-family="Arial, sans-serif" font-size="22" font-weight="700" letter-spacing="4" fill="#6a7b75">FIBER</text>
    <text x="34" y="145" font-family="Arial, sans-serif" font-size="48" font-weight="700" fill="#c58d1f">${fiber}</text>
    <text x="34" y="202" font-family="Arial, sans-serif" font-size="24" fill="#6a7b75">Daily guardrail check</text>
  </g>
  <g transform="translate(76 790)">
    <rect width="928" height="300" rx="36" fill="#12362f"/>
    <text x="38" y="66" font-family="Arial, sans-serif" font-size="22" font-weight="700" letter-spacing="4" fill="#e5b53e">ONE FOOD FIX</text>
    <text x="38" y="145" font-family="Georgia, serif" font-size="48" font-weight="700" fill="#ffffff">${fixLineOne}</text>
    <text x="38" y="210" font-family="Georgia, serif" font-size="48" font-weight="700" fill="#ffffff">${fixLineTwo}</text>
  </g>
  <text x="76" y="1175" font-family="Arial, sans-serif" font-size="24" font-weight="700" fill="#12362f">Two goals. One simple fix.</text>
  <text x="76" y="1224" font-family="Arial, sans-serif" font-size="22" fill="#6a7b75">General wellness estimate • not medical advice</text>
  <text x="76" y="1282" font-family="Arial, sans-serif" font-size="25" font-weight="700" fill="#12362f">proteinfiberguard.com</text>
</svg>`;
};

export const shareCardPreviewUrl = (input: ShareCardInput): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(createShareCardSvg(input))}`;

const createPngBlob = async (input: ShareCardInput): Promise<Blob> => {
  const svgBlob = new Blob([createShareCardSvg(input)], { type: "image/svg+xml;charset=utf-8" });
  const sourceUrl = URL.createObjectURL(svgBlob);
  try {
    const image = new Image();
    image.decoding = "async";
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("Could not render the share card."));
      image.src = sourceUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = 1080; canvas.height = 1350;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Image export is unavailable in this browser.");
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Could not export the share card.")), "image/png"));
  } finally { URL.revokeObjectURL(sourceUrl); }
};

export const downloadShareCard = async (input: ShareCardInput): Promise<void> => {
  const blob = await createPngBlob(input);
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url; link.download = "protein-fiber-guard-result.png";
    document.body.appendChild(link); link.click(); link.remove();
  } finally { window.setTimeout(() => URL.revokeObjectURL(url), 1000); }
};

export const shareResultCard = async (input: ShareCardInput): Promise<"shared" | "downloaded" | "cancelled"> => {
  const blob = await createPngBlob(input);
  const file = new File([blob], "protein-fiber-guard-result.png", { type: "image/png" });
  const shareData = { title: "My Protein & Fiber Guard", text: "Two goals. One simple fix.", url: SHARE_CARD_URL };
  try {
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      await navigator.share({ ...shareData, files: [file] });
      return "shared";
    }
    if (navigator.share) {
      await navigator.share(shareData);
      return "shared";
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return "cancelled";
    throw error;
  }
  await downloadShareCard(input);
  return "downloaded";
};
